<template>
  <div class="wrapper" @touchmove="onAppTouchMove" ref="screenerMainContent">
    <div class="header">
      <div class="logo-container">
        <MableLogo class="logo" />
      </div>
      <div v-if="views.showScreenerQuestionsView" class="right">
        <MuteButtonComponent v-if="screenerData?.metadata?.mediaSettings?.canBeMuted === true" />
        <StarsCounterComponent
          v-if="screenerData?.metadata?.additionalSettings?.disableRewards !== true"
        />
      </div>
    </div>
    <div class="contentWrapper" :class="{ 'overflow-y-auto': views.showStatusView }">
      <DemoControls v-if="demoControlsAvailable" />

      <ErrorModal
        :show="views.showErrorView"
        :error-msg="errorMsg"
        :title="errorModalTitle"
        :is-error-view-screen="true"
        @re-enter-code="clearCode"
      />

      <AudioSettingsModal
        v-if="requiresAudioDevicePrompt && !screenerCompleted && !views.showErrorView"
        @tech-test-finished="onTechTestFinished"
        :key="`audio-settings-${audioResetKey}`"
      />

      <CodeInputView
        v-if="views.showCodeInputView"
        :initialCode="code"
        :error-msg="errorMsg"
        :checking-code="checkingCode"
        :regenerate-screener-id="!!expiredScreenerId"
        @code-updated="errorMsg.message = ''"
        @code-entered="onCodeEntered"
        @re-enter-code="clearCode"
        @regenerate-code="regenerateCode"
      />

      <RegeneratedCodeNotice
        v-if="regeneratedAccessCode"
        :code="regeneratedAccessCode"
        @start-screener="onCodeEntered(regeneratedAccessCode)"
      />

      <StartScreenerView
        v-if="views.showStartScreenerView"
        @re-enter-code="clearCode"
        @start-screener="startQuestionnaire"
      />

      <ScreenerQuestionsView
        v-if="views.showScreenerQuestionsView"
        @screening-finished="onScreenerFinished"
        @screening-paused="onScreenerPaused"
        :key="demoProgressKey"
      />

      <ScreenerPausedView v-if="views.showPausedScreenerView" />

      <StaggerCountdownView v-if="views.showStaggerCountdownView" @stagger-ended="onStaggerEnded" />

      <StatusView v-if="views.showStatusView" @continue-anyway="onContinueAnyway" />

      <div v-if="views.showScreenerQuestionsViewLoader" class="loading-screen">
        <h1>Loading...</h1>
        <ProgressBar
          :progress="preloadProgress"
          @onComplete="showComponent('ScreenerQuestionsView')"
        />
      </div>
      <img
        v-if="!backgroundImage.current"
        src="@/assets/images/CornerImage.svg"
        alt=""
        class="no-select branded-background"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// Vue
import { useRoute, useRouter } from 'vue-router'
import { computed, ref, reactive, watch, onUnmounted, onMounted } from 'vue'

// Types
import type { RouteLocationNormalized } from 'vue-router'
import type { ComputedRef, Ref } from 'vue'
import type { ShowView, ViewName, InputMethod, ErrorMessage } from '@/customTypes'

// Stores
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'
import { usePreloadStore } from '@/stores/PreloadStore'
import { useActivityStore } from '@/stores/ActivityStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useEffectsStore } from '@/stores/EffectsStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'
import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'
import { useDevStore } from '@/stores/DevStore'
import { useServiceHealthStore, clearFirebaseFailure } from '@/stores/ServiceHealthStore'

// Services
import NoSleep from '@/services/NoSleep.js'

// Icons

// Components
import MableLogo from '@/components/MableLogo.vue'
import AudioSettingsModal from '@/components/AudioSettingsModal.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import MuteButtonComponent from '@/components/MuteButtonComponent.vue'
import DemoControls from '@/components/DemoControls.vue'
import RegeneratedCodeNotice from '@/components/RegeneratedCodeNotice.vue'
import ErrorModal from '@/components/ErrorModal.vue'

// Views
import CodeInputView from '@/views/CodeInputView.vue'
import StartScreenerView from '@/views/StartScreenerView.vue'
import ScreenerQuestionsView from '@/views/ScreenerQuestionsView.vue'
import StarsCounterComponent from '@/components/StarsCounterComponent.vue'
import ScreenerPausedView from '@/views/ScreenerPausedView.vue'
import StaggerCountdownView from '@/views/StaggerCountdownView.vue'
import StatusView from '@/views/StatusView.vue'
import Logger from '@/services/Logger.js'

const logger: any = new Logger('MableScreener.vue', true)

const props = defineProps({
  demo: {
    type: Boolean,
    required: false
  },
  emailScreenerId: {
    type: String,
    required: false
  }
})

// Stores
const preloadStore = usePreloadStore()
const dataStore = useDataStore()
const mediaDevicesStore = useMediaDevicesStore()
const effectsStore = useEffectsStore()
const { backgroundImage } = storeToRefs(effectsStore)
const { preloadProgress } = storeToRefs(preloadStore)
const { screenerData, screenerCompleted, screenerError, pausedState, demoProgressKey } =
  storeToRefs(dataStore)
const devStore = useDevStore()
const { showDemoControls } = storeToRefs(devStore)
const activityStore = useActivityStore()
const soundEffectsStore = useSoundEffectsStore()
const { audioContext } = storeToRefs(soundEffectsStore)
const audioAnalyserStore = useAudioAnalyserStore()
const screenerMainContent: any = ref(null)
const audioResetKey = ref<number>(0)
let expiredScreenerId = ref<number>()
const regeneratedAccessCode = ref<string>()

const serviceHealthStore = useServiceHealthStore()
const { failureOverrideEnabled } = storeToRefs(serviceHealthStore)

const enforceFirewallBlock = _ENFORCE_FIREWALL_BLOCK_ === 'true'
const FIREWALL_CHECK_STORAGE_KEY = 'mable:firewall-check-state'
const FIREWALL_CHECK_INTERVAL_MS = 60 * 60 * 1000

type HealthStatus = 'unknown' | 'healthy' | 'unhealthy'

interface FirewallCheckState {
  timestamp: number
  code: string | null
  deepgram: HealthStatus
  firebase: HealthStatus
  api: HealthStatus
  s3: HealthStatus
}

const noSleep: any = ref(null)
const activeScreenerStore = useActiveScreenerStore()

// Router
const router = useRouter()
const route: RouteLocationNormalized = useRoute()
let authorizedToReconnect = false

// Refs - views
const views: ShowView = reactive({
  showErrorView: false,
  showCodeInputView: false,
  showStartScreenerView: false,
  showScreenerQuestionsViewLoader: false,
  showScreenerQuestionsView: false,
  showPausedScreenerView: false,
  showStaggerCountdownView: false,
  showStatusView: false
})

const errorMsg: Ref<ErrorMessage> = ref({
  message: '',
  type: 'danger'
})
const defaultErrorMsg: Ref<string> = ref(
  'Something went wrong when trying to get the screener data.\n Please contact the support team.'
)
const checkingCode: Ref<boolean> = ref(false)

const { requiresAudioDevicePrompt } = storeToRefs(mediaDevicesStore)

// Computed
const code: ComputedRef<string> = computed(() => route.params.code as string)

const errorModalTitle = computed(() => {
  if (errorMsg.value.metadata?.errorCategory === 'network') return 'Network Error'
  if (errorMsg.value.metadata?.originalType === 'Offline') return 'Network Error'
  return ''
})

const demoControlsAvailable: ComputedRef<Boolean> = computed(() => {
  return (
    showDemoControls.value &&
    (views.showErrorView ||
      views.showStartScreenerView ||
      views.showScreenerQuestionsView ||
      views.showPausedScreenerView)
  )
})

/**
 * Watches for the audioContext to be initialized and then enables the NoSleep feature
 */
const stopWatch = watch(audioContext, (newValue) => {
  if (newValue) {
    noSleep.value = new NoSleep(useSoundEffectsStore)
    initializeNoSleep()
    stopWatch()
  }
})

/**
 * In case there is an errors sending the answers to the server, stop the activity and show the error view
 */
watch(screenerError, (errorMessage) => {
  if (errorMessage) {
    logger.error(
      'Screener error occurred with:',
      errorMessage.type,
      ' message:',
      errorMessage.message
    )
    errorMsg.value = {
      message: errorMessage.message,
      type: 'danger',
      metadata: errorMessage.metadata
    }
    showComponent('ErrorView')
  }
})

/**
 * Watches for the paused state and shows the paused view if the screener is paused
 */
watch(pausedState, (paused) => {
  if (!paused) {
    showComponent('ScreenerQuestionsView')
  }
})

let previousView: ViewName | null = null

const enforceFirewallStatus = (hasFailures: boolean, overrideEnabled: boolean) => {
  if (serviceHealthStore.isRunningChecks) {
    return
  }

  if (!hasFailures) {
    if (overrideEnabled) {
      serviceHealthStore.setFailureOverride(false)
    }
    if (views.showStatusView && previousView) {
      showComponent(previousView)
      previousView = null
    }
    return
  }

  if (!enforceFirewallBlock && overrideEnabled) {
    return
  }

  if (!views.showStatusView) {
    activeScreenerStore.redirectFrom = {
      name: typeof route.name === 'string' ? route.name : undefined,
      params: route.params,
      query: route.query
    }
    showComponent('StatusView')
  }
}

watch(
  [
    () => serviceHealthStore.hasFailures,
    () => failureOverrideEnabled.value,
    () => serviceHealthStore.isRunningChecks
  ],
  ([hasFailures, overrideEnabled]) => {
    enforceFirewallStatus(hasFailures, overrideEnabled)
  }
)

watch(
  () => props.demo,
  () => {
    devStore.setDemoSession(props.demo)
  },
  { immediate: true }
)

/**
 * Prevents the app from scrolling/refreshing/going back when the user is touching the screen
 * Allows scrolling within scrollable containers (like StatusView)
 * @param event
 */
const onAppTouchMove = (event: TouchEvent) => {
  const target = event.target as HTMLElement
  const scrollableParent = target.closest('.status-card')

  if (scrollableParent) {
    return
  }

  event.preventDefault()
}

const getFirewallCheckState = (): FirewallCheckState | null => {
  if (typeof window === 'undefined') return null
  try {
    const rawState = window.localStorage.getItem(FIREWALL_CHECK_STORAGE_KEY)
    if (!rawState) return null
    const parsed = JSON.parse(rawState) as FirewallCheckState
    const parsedCode = typeof parsed.code === 'string' && parsed.code.length ? parsed.code : null
    const state: FirewallCheckState = {
      timestamp: typeof parsed.timestamp === 'number' ? parsed.timestamp : 0,
      code: parsedCode,
      deepgram: parsed.deepgram || 'unknown',
      firebase: parsed.firebase || 'unknown',
      api: parsed.api || 'unknown',
      s3: parsed.s3 || 'unknown'
    }
    // Restore statuses to the store
    serviceHealthStore.deepgramStatus = state.deepgram
    serviceHealthStore.firebaseStatus = state.firebase
    serviceHealthStore.apiStatus = state.api
    serviceHealthStore.s3Status = state.s3
    return state
  } catch (error) {
    window.localStorage.removeItem(FIREWALL_CHECK_STORAGE_KEY)
    logger.warn('Failed to parse firewall check state', error)
    return null
  }
}

const persistFirewallCheckState = (code: string | null) => {
  if (typeof window === 'undefined' || enforceFirewallBlock) return
  const state: FirewallCheckState = {
    timestamp: Date.now(),
    code,
    deepgram: serviceHealthStore.deepgramStatus,
    firebase: serviceHealthStore.firebaseStatus,
    api: serviceHealthStore.apiStatus,
    s3: serviceHealthStore.s3Status
  }
  window.localStorage.setItem(FIREWALL_CHECK_STORAGE_KEY, JSON.stringify(state))
}

const removeFirewallCheckState = () => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(FIREWALL_CHECK_STORAGE_KEY)
}

const runFirewallChecks = async (code: string | null = null) => {
  try {
    await serviceHealthStore.runChecks()
    persistFirewallCheckState(code)
  } catch (error) {
    logger.error('Service health check failed', error)
    removeFirewallCheckState()
  }
}

const maybeRunFirewallChecks = (codeForCheck?: string | null): Promise<void> => {
  const activeCode = codeForCheck ?? null
  if (enforceFirewallBlock || typeof window === 'undefined') {
    return runFirewallChecks(activeCode)
  }

  const existingState = getFirewallCheckState()
  if (!existingState) {
    return runFirewallChecks(activeCode)
  }

  const isExpired = Date.now() - existingState.timestamp >= FIREWALL_CHECK_INTERVAL_MS
  const storedCode = existingState.code
  const hasNewCode = Boolean(activeCode && storedCode && storedCode !== activeCode)

  if (isExpired || hasNewCode) {
    return runFirewallChecks(activeCode)
  }

  return Promise.resolve()
}

const initialize = async (): Promise<void> => {
  // Login to Firebase
  try {
    await dataStore.login()
  } catch (error) {
    logger.error('Error logging into Firebase', error)
    serviceHealthStore.firebaseStatus = 'unhealthy'
    showComponent('StatusView')
    return
  }

  await maybeRunFirewallChecks(code.value)

  if (route.name === 'status') {
    showComponent('StatusView')
    return
  }

  if (!serviceHealthStore.hasFailures) {
    showComponent('CodeInputView')
  }
}

const initializeNoSleep = async (): Promise<void> => {
  if (!noSleep.value) return
  document.addEventListener(
    'click',
    function enableNoSleep() {
      document.removeEventListener('click', enableNoSleep, false)
      noSleep.value.enable()
    },
    false
  )
}

/**
 * Fetches the screener data from the API using the code from the user input
 * @param {string} code - The code provided by the user
 */
const onCodeEntered = async (code: string): Promise<void> => {
  try {
    errorMsg.value.message = ''
    checkingCode.value = true
    await fetchScreenerData(code, 'manual')

    // Always re-run now the pupil is signed in: any earlier result was checked without a session,
    // so it skipped the Firebase write and the live Deepgram connection.
    await runFirewallChecks(code)
    const { deepgramStatus, firebaseStatus, apiStatus, s3Status } = storeToRefs(serviceHealthStore)
    await activeScreenerStore.logToDatabase('firewall_check', {
      deepgram: deepgramStatus.value,
      firebase: firebaseStatus.value,
      api: apiStatus.value,
      s3: s3Status.value
    })
    // push the code to the URL so the user can refresh the page
    router.replace({ params: { code } })
    regeneratedAccessCode.value = undefined
    expiredScreenerId.value = undefined
  } catch (error: any) {
    regeneratedAccessCode.value = undefined
    logger.error('Error fetching screener data', error)

    if (error.status === 409 && !authorizedToReconnect) {
      activeScreenerStore.watchAccessRequest(error.screenerId, error.roomId, error.deviceId, () => {
        authorizedToReconnect = true
        onCodeEntered(code)
      })
      showComponent('CodeInputView')
      errorMsg.value.type = 'warning'
      errorMsg.value.message = error?.message || defaultErrorMsg.value
    } else {
      showComponent('CodeInputView')
      errorMsg.value.type = 'danger'
      errorMsg.value.message = error?.message || defaultErrorMsg.value
    }

    if (parseInt(props.emailScreenerId!) === error.screenerId) {
      expiredScreenerId.value = error.screenerId
    }
  }
  checkingCode.value = false
}

const regenerateCode = async (): Promise<void> => {
  errorMsg.value.message = ''
  const emailScreenerId = parseInt(props.emailScreenerId!) as number
  if (emailScreenerId === expiredScreenerId.value) {
    try {
      const response = (await dataStore.regenerateCode(emailScreenerId, code.value)) as any
      regeneratedAccessCode.value = response.code.toString()
    } catch (error) {
      errorMsg.value.type = 'danger'
      errorMsg.value.message =
        'Error regenerating code, the code may need regenerating manually through the mable admin panel. \n If the problem persists, please contact the support team.'
    }
  } else {
    errorMsg.value.type = 'danger'
    errorMsg.value.message =
      'Error regenerating code, the code may need regenerating manually through the mable admin panel. \n If the problem persists, please contact the support team.'
  }
}

/**
 * Resets the code input view
 */
const clearCode = async (): Promise<void> => {
  // remove the code from the URL
  errorMsg.value = { message: '', type: 'danger' }
  await router.push({ name: 'home', params: { code: '' } })
  activityStore.stop()
  soundEffectsStore.stopTracks()
  showComponent('CodeInputView')
  resetRecordingSetup()
  dataStore.$reset()
}

const { exitRequestedAt } = storeToRefs(activeScreenerStore)
watch(exitRequestedAt, async (requestedAt) => {
  if (!requestedAt) {
    return
  }
  await clearCode()
})

/**
 * Fetches the screener data from the API
 * @param {string} code - The code provided by the user
 * @param {InputMethod} method - The method used to fetch the data
 */
const fetchScreenerData = async (code: string, method: InputMethod): Promise<void> => {
  if (mediaDevicesStore.microphonePermissionRequired === false) {
    showComponent('ScreenerQuestionsViewLoader')
  }

  await dataStore.fetchScreenerData(code, method, props.demo)
  await mediaDevicesStore.setupScreenerMedia()

  activityStore.initiate()

  // If some question been answered already and page was reloaded, show the questions view
  if (
    route.meta?.wasReloaded &&
    dataStore.pupilAnswers.length > 0 &&
    !dataStore.screenerCompleted &&
    requiresAudioDevicePrompt.value === false
  ) {
    startQuestionnaire()
  } else if (
    dataStore.screenerCompleted ||
    mediaDevicesStore.microphonePermissionRequired === false
  ) {
    showComponent('StartScreenerView')
    await preloadStore.preloadScreenerData()
  } else {
    mediaDevicesStore.setRequiredAudioDevicePrompt(true, true)
  }
}

const onTechTestFinished = async () => {
  mediaDevicesStore.setRequiredAudioDevicePrompt(false)
  showComponent('StartScreenerView')
  await preloadStore.preloadScreenerData()
}

const startQuestionnaire = async (): Promise<void> => {
  // if loading from refresh show preload loader
  if (preloadProgress.value >= 100 && views.showStartScreenerView) {
    showComponent('ScreenerQuestionsView')
  } else {
    showComponent('ScreenerQuestionsViewLoader')
    await preloadStore.preloadScreenerData()
  }
  route.meta.wasReloaded = false
}

/**
 * Shows the component based on the provided name
 * @param {ViewName} componentName - The name of the component to show
 */
const showComponent = (componentName: ViewName): void => {
  if (componentName !== 'StatusView') {
    previousView = componentName
  }

  Object.keys(views).forEach((key) => {
    views[key as keyof ShowView] = false
  })

  if (componentName === 'ScreenerQuestionsView') {
    if (pausedState.value) {
      soundEffectsStore.clearAllLoadErrorTimeouts()
      views.showPausedScreenerView = true
      activeScreenerStore.sendSectionChange({ section: 'PausedScreenerView' })
      return
    }
  }

  views[`show${componentName}`] = true
  dataStore.setScreenerInProgress(componentName === 'ScreenerQuestionsView')
  activeScreenerStore.sendSectionChange({ section: componentName })
}

/**
 * Callback function when the screener is finished
 */
const onScreenerFinished = () => {
  dataStore.setScreenerCompleted(true)
  showComponent('StartScreenerView')
}

const { staggerEndsAt } = storeToRefs(activeScreenerStore)
watch(staggerEndsAt, (endsAt) => {
  if (typeof endsAt === 'number' && !views.showStaggerCountdownView) {
    showComponent('StaggerCountdownView')
  }
})

const onStaggerEnded = async () => {
  const action = activeScreenerStore.pendingStaggerAction
  await activeScreenerStore.consumeStagger()
  if (action === 'start') {
    startQuestionnaire()
  } else {
    showComponent('ScreenerQuestionsView')
  }
}

/**
 * Callback function when the screener is paused
 */
const onScreenerPaused = () => {
  showComponent('PausedScreenerView')
  effectsStore.$reset()
}

const resetRecordingSetup = () => {
  soundEffectsStore.clearAllLoadErrorTimeouts()
  mediaDevicesStore.closeRecordingStream()
  audioAnalyserStore.destroyAudioAnalyser()
  soundEffectsStore.unmountHowler()
}

const onContinueAnyway = () => {
  const viewToShow = previousView || 'CodeInputView'
  previousView = null
  showComponent(viewToShow)
}

onUnmounted(() => {
  dataStore.clearFullscreenElement()
  resetRecordingSetup()
  activeScreenerStore.disconnect()
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', clearFirebaseFailure)
  }
})

onMounted(() => {
  dataStore.setFullscreenElement(screenerMainContent.value)
  if (typeof window !== 'undefined') {
    window.addEventListener('online', clearFirebaseFailure)
  }
  enforceFirewallStatus(serviceHealthStore.hasFailures, failureOverrideEnabled.value)
  initialize()
})
</script>

<style lang="scss" scoped>
.wrapper {
  width: var(--full-vw);
  height: var(--full-vh);
  padding: 2rem;
}

.header {
  display: flex;
  width: calc(var(--full-vw) -4rem);
  height: calc(var(--full-vh) * 0.07);
  justify-content: space-between;
  align-items: center;
  padding: 0;
  z-index: 1;
  position: relative;
  z-index: 1;

  .right {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.contentWrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  padding-top: 0;
  //  100dvh - Mable logo height (header) - app padding
  height: calc(var(--full-vh) - calc(var(--full-vh) * 0.07) - 2rem);
}

.overflow-y-auto {
  position: absolute;
  width: var(--full-vw);
  height: var(--full-vh);

  max-height: 100dvh;
  padding-top: 2rem;
  top: 0;
  left: 0;
}

@media screen and (orientation: portrait) and (hover: none) {
  .wrapper {
    transform: rotate(90deg);
    @include prefixed(transform-origin, right top);
    overflow: hidden;
    position: fixed;
    top: var(--full-vw);
    right: 0;
  }
}

.loading-screen {
  width: calc(var(--full-vw) * 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  color: $secondary;
}

.branded-background {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 0;
  pointer-events: none;
  width: calc(600px * ((var(--full-vw) - 400px) / 1200));
  max-width: calc(var(--full-vw) * 0.4);
}

:deep(.status-card) {
  position: relative;
  z-index: 2;
}
</style>
